import { Avatar, FloatingLoader, Loading, Typography } from "@/components";
import { SearchInput } from "@/components/input";
import { BackAppBar } from "@/components/ui";
import useBackward from "@/hooks/useBackward";
import useCaseMutation from "@/hooks/useCaseMutation";
import { CASE_MEMBER_TYPE, QUERY_KEYS } from "@/libraries/constant";
import { isAxiosError, isMpisAccount, pluralize } from "@/libraries/helper";
import {
	getCaseQueryOptions,
	getMembersQueryOptions,
	getMpisMembersQueryOptions,
	getTeamsQueryOptions,
} from "@/queries";
import { InviteCaseMemberSearchParamSchema } from "@/schemas";
import type { CaseTeam, ContactMemberResponse } from "@/types";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import {
	onlineManager,
	useSuspenseInfiniteQuery,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { ErrorComponent, createFileRoute } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import compact from "lodash/compact";
import omit from "lodash/omit";
import uniqBy from "lodash/uniqBy";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import ForbiddenAccessMembers from "./-components/ForbiddenAccessMembers";
import PendingComponentMembers from "./-components/PendingComponentMembers";

export const Route = createFileRoute("/dashboard/case/view/$id/members/")({
	validateSearch: zodSearchValidator(InviteCaseMemberSearchParamSchema),
	loaderDeps: ({ search }) => ({
		tab: search.tab,
		subTab: search.subtab,
	}),
	loader: ({ context, params, deps }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return [
				context.queryClient.ensureQueryData(getCaseQueryOptions(params.id)),
				...(deps.tab !== "mpis"
					? [
							context.queryClient.ensureInfiniteQueryData(
								getMembersQueryOptions({
									directory: deps.tab === "directory",
								}),
							),
							context.queryClient.ensureInfiniteQueryData(
								getTeamsQueryOptions({
									directory: deps.tab === "directory",
									scope: "case_teams",
								}),
							),
						]
					: [
							context.queryClient.ensureInfiniteQueryData(
								getMpisMembersQueryOptions(),
							),
						]),
			];
		}
		return [
			context.queryClient.getQueryData([QUERY_KEYS.GET_CASE, params.id]),
			...(deps.tab !== "mpis"
				? [
						context.queryClient.getQueryData([
							QUERY_KEYS.GET_MEMBERS,
							{
								directory: deps.tab === "directory",
							},
						]),
						context.queryClient.getQueryData([
							QUERY_KEYS.GET_TEAMS,
							{
								directory: deps.tab === "directory",
								scope: "case_teams",
							},
						]),
					]
				: [context.queryClient.getQueryData([QUERY_KEYS.GET_MPIS_MEMBERS])]),
		];
	},
	component: RouteComponent,
	pendingComponent: PendingComponentMembers,
	errorComponent: ({ error }) => {
		if (error && isAxiosError(error, 403)) {
			return <ForbiddenAccessMembers />;
		}
		return <ErrorComponent error={error} />;
	},
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const search = Route.useSearch({
		select: (s) => s.search || "",
	});
	const caseId = Route.useParams({
		select: (s) => s.id,
	});
	const tab = Route.useLoaderDeps({
		select: (s) => s.tab as "mpis" | "contact" | "directory",
	});
	const subTab = Route.useLoaderDeps({
		select: (s) => s.subTab as "team" | "contact",
	});
	const scrollRef = useRef<HTMLDivElement>(null);
	const onBack = useBackward();

	const caseQuery = useSuspenseQuery(getCaseQueryOptions(caseId));

	const currentQueryOption = useMemo(
		() =>
			tab !== "mpis"
				? getMembersQueryOptions({
						directory: tab === "directory",
					})
				: getMpisMembersQueryOptions(),
		[tab],
	);

	const contactsQuery = useSuspenseInfiniteQuery(currentQueryOption as any);
	const teamsQuery = useSuspenseInfiniteQuery(
		getTeamsQueryOptions({
			directory: tab === "directory",
			scope: "case_teams",
		}),
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const currentQuery = useMemo(
		() =>
			tab === "mpis"
				? contactsQuery
				: subTab === "contact"
					? contactsQuery
					: teamsQuery,
		[tab, subTab],
	);

	const alreadyInvitedTeams = useMemo(
		() =>
			Array.from(
				new Set([
					...caseQuery.data.pending_teams.map((i) => i.id),
					...caseQuery.data.teams.map((i) => i.id),
				]),
			),
		[caseQuery.data],
	);

	const alreadyInvitedContacts = useMemo(
		() =>
			uniqBy(
				[
					...caseQuery.data.pending_invitations,
					...caseQuery.data.external_pending_invitations,
					...caseQuery.data.internal_members,
					...caseQuery.data.external_members.filter(
						(member) =>
							member.member_type !== CASE_MEMBER_TYPE.EXTERNAL_REQUEST_MEMBER,
					),
				],
				(item) => item.id || item.email,
			),
		[caseQuery.data],
	);

	const teams = useMemo<CaseTeam[]>(
		() => teamsQuery.data.pages.flatMap((p) => p.teams ?? []) as CaseTeam[],
		[teamsQuery.data],
	);
	const contacts = useMemo(
		() => contactsQuery.data as unknown as ContactMemberResponse["users"],
		[contactsQuery.data],
	);

	const listItems = useMemo(() => {
		const cleanedTeams = teams.filter(
			(team) => !alreadyInvitedTeams.includes(team.id),
		);

		if (subTab === "team" && !search) {
			return cleanedTeams;
		}

		if (subTab === "team" && search) {
			return cleanedTeams.filter((team) =>
				team.name?.toLowerCase().includes(search.toLowerCase()),
			);
		}

		const cleanedContacts = contacts.filter(
			(contact) =>
				!alreadyInvitedContacts.some(
					(invited) =>
						invited.id === contact.id || invited.email === contact.email,
				),
		);

		if (subTab === "contact" && !search) {
			return cleanedContacts;
		}

		if (subTab === "contact" && search) {
			return cleanedContacts.filter((contact) =>
				contact.fullname?.toLowerCase().includes(search.toLowerCase()),
			);
		}

		return [];
	}, [
		subTab,
		teams,
		contacts,
		alreadyInvitedTeams,
		alreadyInvitedContacts,
		search,
	]);

	const isLoading = useMemo(
		() =>
			contactsQuery.isLoading ||
			teamsQuery.isLoading ||
			contactsQuery.isFetchingNextPage ||
			teamsQuery.isFetchingNextPage,
		[
			contactsQuery.isLoading,
			teamsQuery.isLoading,
			contactsQuery.isFetchingNextPage,
			teamsQuery.isFetchingNextPage,
		],
	);

	const { addMembers, assignTeam } = useCaseMutation();

	const {
		watch,
		setValue,
		handleSubmit,
		formState: { isSubmitting },
	} = useForm<{
		caseId: number;
		teams: number[];
		contacts: (string | number)[];
	}>({
		mode: "all",
		defaultValues: {
			caseId,
			teams: [],
			contacts: [],
		},
	});

	const selectedContactIds = watch("contacts");
	const selectedTeamIds = watch("teams");
	const selectedIds = useMemo(
		() => (subTab === "contact" ? selectedContactIds : selectedTeamIds),
		[subTab, selectedContactIds, selectedTeamIds],
	);

	const rowVirtualizer = useVirtualizer({
		count: listItems.length,
		overscan: 5,
		estimateSize: () => 58,
		getScrollElement: () => scrollRef.current,
		measureElement: (el) => el.getBoundingClientRect().height,
	});
	const rowVirtualizerItems = rowVirtualizer.getVirtualItems();

	const renderListItem = useCallback(
		(listItem: (typeof listItems)[number]) => {
			const isTeam = "type" in listItem && listItem.type === "team";
			// @ts-expect-error
			const name = isTeam ? listItem.name : listItem.fullname;
			// @ts-expect-error
			const id = listItem.id ?? listItem.aidbox_id;
			const isChecked = selectedIds.some((i) => i === id);
			return (
				<ListItemButton
					component="label"
					htmlFor={`invite-${id}`}
					classes={{
						root: isChecked ? "!bg-primary-light" : undefined,
					}}
				>
					<ListItemIcon>
						<Checkbox
							disableRipple
							edge="start"
							tabIndex={-1}
							inputProps={{
								"aria-labelledby": name,
								onChange: (e) =>
									setValue(
										isTeam ? "teams" : "contacts",
										e.target.checked
											? Array.from(new Set([...selectedIds, id]))
											: selectedIds.filter((item) => item !== id),
									),
							}}
							id={`invite-${id}`}
							name={isTeam ? "teams[]" : "contacts[]"}
							value={id}
							checked={isChecked}
						/>
					</ListItemIcon>
					{!isTeam ? (
						<ListItemAvatar>
							<Avatar
								// @ts-expect-error
								alt={listItem.first_name || "L"}
								className="!bg-gray-light !text-dark"
								// @ts-expect-error
								src={listItem.avatar ?? "/broken-image.jpg"}
							/>
						</ListItemAvatar>
					) : null}
					<ListItemText
						id={id.toString()}
						primary={name}
						secondary={
							isTeam ? `${listItem.member_count} team members` : undefined
						}
					/>
				</ListItemButton>
			);
		},
		[selectedIds, setValue],
	);

	const handleChangeTab = (e: React.SyntheticEvent, newValue: string) => {
		e.preventDefault();
		return navigate({
			search: {
				tab: newValue,
				subtab: newValue === "mpis" ? "contact" : undefined,
			},
			replace: true,
		}).then(() => rowVirtualizer.scrollToIndex(0));
	};

	const handleChangeSubTab = (e: React.SyntheticEvent, newValue: string) => {
		e.preventDefault();
		return navigate({
			search: {
				tab: tab,
				subtab: newValue,
			},
			replace: true,
		}).then(() => rowVirtualizer.scrollToIndex(0));
	};

	const onSubmit = useCallback<
		SubmitHandler<{
			caseId: number;
			teams: number[];
			contacts: (string | number)[];
		}>
	>(
		async (data, e) => {
			e?.preventDefault();
			e?.stopPropagation();

			const { caseId, contacts, teams } = data;

			const navigateOrBack = () => {
				if (!caseQuery.data.encounter_id) return onBack();
				navigate({
					replace: true,
					to: "/dashboard/case/view/$id",
					params: { id: caseId },
				});
			};

			if (contacts.length) {
				const user_ids = contacts.filter(
					(id): id is number => typeof id === "number",
				);
				const user_aidbox_ids = contacts.filter(
					(id): id is string => typeof id === "string",
				);

				try {
					await addMembers.mutateAsync({ caseId, user_ids, user_aidbox_ids });
					toast.success("Members successfully invited as Internal Members.");
					return navigateOrBack();
				} catch (err) {
					if (isAxiosError(err, 422)) {
						// @ts-expect-error
						return toast.error(err.response?.data?.messages);
					}
					toast.error((err as any).message);
				}
			}

			if (teams.length) {
				const results = await Promise.allSettled(
					teams.map((teamId) =>
						assignTeam.mutateAsync({ caseId, teamId }).catch((err) => {
							if (isAxiosError<Record<"messages", string>>(err, 404)) {
								toast.error(err.response?.data?.messages);
							} else {
								toast.error(err.message);
							}
							return Promise.reject(); // ensure rejected for consistent `fulfilled` counting
						}),
					),
				);

				const successful = results.filter((r) => r.status === "fulfilled");
				if (successful.length) {
					toast.success(
						`${successful.length} Team successfully invited as Internal Members.`,
					);
					return navigateOrBack();
				}
			}
		},
		[addMembers, assignTeam, navigate, caseQuery.data, onBack],
	);

	useEffect(() => {
		const [lastItem] = [...rowVirtualizerItems].reverse();

		if (!lastItem) return;

		if (
			tab !== "mpis" &&
			lastItem.index >= listItems.length - 1 &&
			currentQuery.hasNextPage &&
			!currentQuery.isFetchingNextPage
		) {
			currentQuery.fetchNextPage();
		}
	}, [
		tab,
		listItems.length,
		currentQuery.hasNextPage,
		currentQuery.fetchNextPage,
		currentQuery.isFetchingNextPage,
		rowVirtualizerItems,
	]);

	return (
		<>
			<BackAppBar title="Invite Member">
				<div className="container pb-2 space-y-4">
					<Tabs
						centered
						value={tab}
						variant="fullWidth"
						textColor="inherit"
						aria-label="Contact Type"
						className="!rounded-full !bg-gray-light [&_.MuiTabs-indicator]:!bg-transparent !p-1"
						onChange={handleChangeTab}
					>
						<Tab
							value="contact"
							label="Contacts"
							classes={{
								selected: "!bg-white !rounded-full",
							}}
						/>
						<Tab
							value="directory"
							label="Directory"
							classes={{
								selected: "!bg-white !rounded-full",
							}}
						/>
						{isMpisAccount() ? (
							<Tab
								value="mpis"
								label="MPIS"
								classes={{
									selected: "!bg-white !rounded-full",
								}}
							/>
						) : null}
					</Tabs>
					{tab !== "mpis" ? (
						<Tabs
							value={subTab}
							variant="fullWidth"
							textColor="inherit"
							aria-label="Contact Sub Type"
							className="[&_.MuiTabs-indicator]:bg-primary"
							onChange={handleChangeSubTab}
						>
							<Tab
								value="team"
								label="Teams"
								style={{ textTransform: "none" }}
							/>
							<Tab
								value="contact"
								label="Contacts"
								style={{ textTransform: "none" }}
							/>
						</Tabs>
					) : null}
					<SearchInput
						name="search"
						label="Search"
						placeholder="Search"
						value={search}
						onChange={(e) =>
							navigate({
								// @ts-expect-error
								search: (oldSearch) => ({
									...omit(oldSearch, ["search"]),
									search: e.target.value || undefined,
								}),
								replace: true,
							}).then(() => rowVirtualizer.scrollToIndex(0))
						}
					/>
				</div>
			</BackAppBar>
			<div className="relative flex-1 bg-white overflow-y-hidden">
				<FloatingLoader open={isLoading} />
				<div ref={scrollRef} className="h-full relative overflow-y-auto">
					<List
						className="!relative"
						style={{
							height: rowVirtualizer.getTotalSize(),
						}}
					>
						{!isLoading && !contacts.length && subTab === "contact" ? (
							<ListItem disablePadding>
								<ListItemText
									primary="No contacts"
									classes={{
										primary: "!text-center !text-gray-200",
									}}
								/>
							</ListItem>
						) : null}
						{!isLoading && !teams.length && subTab === "team" ? (
							<ListItem disablePadding>
								<ListItemText
									primary="No teams"
									classes={{
										primary: "!text-center !text-gray-200",
									}}
								/>
							</ListItem>
						) : null}
						{rowVirtualizerItems.map((rowVirtualizerItem) => {
							const listItem = listItems[rowVirtualizerItem.index];
							if (!listItem) return null;
							return (
								<ListItem
									key={rowVirtualizerItem.key}
									data-index={rowVirtualizerItem.index}
									ref={rowVirtualizer.measureElement}
									disablePadding
									className="!absolute !top-0 !left-0 !w-full"
									style={{
										transform: `translateY(${rowVirtualizerItem.start}px)`,
									}}
								>
									{renderListItem(listItem)}
								</ListItem>
							);
						})}
					</List>
				</div>
			</div>
			{selectedTeamIds.length || selectedContactIds.length ? (
				<form
					onSubmit={handleSubmit(onSubmit)}
					className="relative container py-4 bg-white border-t border-gray-light"
				>
					<div className="grid grid-cols-2 gap-2 justify-between items-center">
						<Typography fontWeight={500}>
							{compact([
								selectedTeamIds.length
									? pluralize({
											count: selectedTeamIds.length,
											singularText: "Team",
										})
									: undefined,
								selectedContactIds.length
									? pluralize({
											count: selectedContactIds.length,
											singularText: "Contact",
										})
									: undefined,
							]).join(" & ")}{" "}
							Selected
						</Typography>
						<Button
							type="submit"
							variant="contained"
							color="primary"
							size="large"
							className="!rounded-full"
							fullWidth
							disabled={isSubmitting}
						>
							Next
						</Button>
					</div>
				</form>
			) : null}
			<Loading status={isSubmitting} />
		</>
	);
}
